import { FC, Suspense, lazy, useEffect, useLayoutEffect } from 'react'
import { PlatformFallback } from '../pages/platform/shared/SuperuserGate'
import { Route, Routes, Navigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { toast } from 'sonner'
import Explore from '../pages/exposures/Explore'
import { useFeature } from '../../Utils/featureFlagEngine'
import { useAuthStore } from '../../store/authStore'
import { TourProvider, useTour } from '../../tour'
import NotFound from '../pages/notFound/NotFound'

// Module scope, not inside PrivateRoutes: lazy() mints a new component type on
// every call, so calling it during render remounted the entire account section
// — losing its state and re-entering Suspense — on each render.
const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))

// Triggers the onboarding tour once when the user is new.
// Must be rendered inside TourProvider.
const OnboardingTrigger: FC = () => {
  const { startTour, isTourActive } = useTour()
  const user = useAuthStore((state) => state.user)
  const requires_organization_setup = useAuthStore((state) => state.userProfile?.requires_organization_setup)

  useEffect(() => {
    const tourEnabled =
      (window as any)?.RUNTIME_CONFIG?.REACT_APP_TOUR === 'on' ||
      process.env.REACT_APP_TOUR === 'on'
    // Start tour on the dashboard — the pre-steps guide the user from there
    if (tourEnabled && user?.is_user_new && !isTourActive && !requires_organization_setup) {
      startTour('onboarding')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_user_new, requires_organization_setup])

  return null
}

const PrivateRoutes = () => {
  const user = useAuthStore((state) => state.user)
  const { getMainRoute } = useFeature()
  const { hasPermission, childrenPermission, getRoutePermission } = useFeature()

  // Get all enabled routes from feature flag engine
  const routes = getRoutePermission()


  // Get attack surface children for nested routes
  const attackSurfaceChildren = childrenPermission('attack_surface')
  const isAttackSurfaceEnabled = hasPermission('attack_surface')

  return (
    <TourProvider>
      <OnboardingTrigger />
      <Routes>
        <Route element={<MasterLayout />}>
          {/* Redirect /auth to default dashboard */}
          <Route path='auth/*' element={<Navigate to={getMainRoute() || "/dashboard"} />} />

          {/* Attack Surface nested routes */}
          {isAttackSurfaceEnabled && (
            <Route path='/attack-surface-explore' element={<Explore />}>
              {attackSurfaceChildren
                .filter((child) => child.route?.includes('/attack-surface-explore/'))
                .map((child) => {
                  const Component = child.component
                  if (!Component) return null
                  const pathSegment = child.route!.split('/').pop() || ''
                  return <Route key={child.id} path={pathSegment} element={<SuspendedView><Component /></SuspendedView>} />
                })}
            </Route>
          )}

          {/* All feature-flagged routes */}
          {routes.map((feature) => {
            if (!feature.route || !feature.component) return null

            // Skip attack surface explore child routes (already handled above)
            if (feature.route.includes('/attack-surface-explore/')) return null

            const Component = feature.component

            // Handle wildcard routes
            if (feature.route.endsWith('/*')) {
              return <Route key={feature.id} path={feature.route} element={<SuspendedView><Component /></SuspendedView>} />
            }

            return <Route key={feature.id} path={feature.route} element={<SuspendedView><Component /></SuspendedView>} />
          })}

          {/* Account page (always available) */}
          <Route
            path='crafted/account/*'
            element={
              <SuspendedView>
                <AccountPage />
              </SuspendedView>
            }
          />
          {/* The console's routes exist only for a superuser; anyone else reaches this. */}
          <Route path='platform/*' element={<PlatformFallback />} />
          {/* Unknown routes: show the Not Found page (keeps the app shell) */}
          <Route path='*' element={<NotFound />} />
        </Route>
      </Routes>
    </TourProvider>
  )
}

interface SuspendedViewProps {
  children: React.ReactNode
}

const SuspendedView: FC<SuspendedViewProps> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')

  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })

  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
